/**
 * Renderiza um `DefCampo` conforme o `tipo`. É o tradutor entre o descriptor
 * declarativo e os componentes de input concretos. O estado do formulário guarda
 * strings (e boolean para `booleano`); a coerção para número/data acontece na hora
 * de montar o corpo (`montarCorpo`), não aqui.
 *
 * `telefone`/`documento`/`placa` já trazem o próprio rótulo — os demais recebem
 * `Label` + controle aqui. `leitura` é campo derivado do sistema: exibido formatado,
 * sem input (o operador vê tudo — "nada fica oculto" — mas não edita o que o sistema
 * deriva).
 */
import { CampoDocumento } from '@/components/CampoDocumento';
import { CampoPlaca } from '@/components/CampoPlaca';
import { CampoTelefone } from '@/components/CampoTelefone';
import { ComboReferencia } from '@/components/crud/ComboReferencia';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { humanizarSeData } from '@/lib/datas';
import { formatar, mascaraDinheiro } from '@/lib/mascaras';
import type { DefCampo, RecursoBase } from '@/lib/recursos/tipos';
import { cn } from '@/lib/utils';

// O select acompanha o campo: mesmo sulco, mesmo foco em ciano. Dois controles vizinhos com
// materiais diferentes é o que faz um formulário parecer remendado.
const classeSelect = cn(
  'h-10 w-full rounded-md border border-border bg-input px-3 text-[15px] text-text',
  'shadow-[var(--input-recuo)]',
  'transition-colors duration-[var(--dur-fast)] outline-none',
  'focus-visible:border-accent focus-visible:shadow-[var(--focus-ring)]',
);

export type ValorCampo = string | boolean;

export function CampoDinamico<T extends RecursoBase>({
  campo,
  valor,
  onChange,
  idPrefixo,
}: {
  campo: DefCampo<T>;
  valor: ValorCampo;
  onChange: (v: ValorCampo) => void;
  idPrefixo: string;
}) {
  const id = `${idPrefixo}-${campo.chave}`;
  const texto = typeof valor === 'string' ? valor : '';

  switch (campo.tipo) {
    case 'telefone':
      return (
        <CampoTelefone rotulo={campo.rotulo} id={id} valor={texto} onChange={(e164) => onChange(e164)} />
      );

    case 'documento':
      return (
        <CampoDocumento
          rotulo={campo.rotulo}
          id={id}
          valor={texto}
          onChange={onChange}
          formato={campo.formato ?? 'cpfcnpj'}
          obrigatorio={campo.obrigatorio}
          placeholder={campo.placeholder}
        />
      );

    case 'placa':
      return (
        <CampoPlaca
          rotulo={campo.rotulo}
          id={id}
          valor={texto}
          onChange={onChange}
          obrigatorio={campo.obrigatorio}
        />
      );

    case 'booleano':
      return (
        <label htmlFor={id} className="flex cursor-pointer items-center gap-3 py-2">
          <input
            id={id}
            type="checkbox"
            checked={valor === true}
            onChange={(e) => onChange(e.target.checked)}
            className="size-4 accent-[var(--primary)]"
          />
          <span className="text-[15px] text-text">{campo.rotulo}</span>
        </label>
      );

    case 'leitura':
      return (
        <Campo id={id} campo={campo}>
          <p className="tipo-dado min-h-10 rounded-md border border-border bg-surface-2/50 px-3 py-2 text-text-secondary">
            {campo.formato ? formatar(texto, campo.formato) : texto ? humanizarSeData(texto) : '—'}
          </p>
        </Campo>
      );

    case 'select':
      return (
        <Campo id={id} campo={campo}>
          <select
            id={id}
            required={campo.obrigatorio}
            value={texto}
            onChange={(e) => onChange(e.target.value)}
            className={classeSelect}
          >
            <option value="">—</option>
            {campo.opcoes?.map((o) => (
              <option key={o.valor} value={o.valor}>
                {o.rotulo}
              </option>
            ))}
          </select>
        </Campo>
      );

    case 'referencia':
      return (
        <Campo id={id} campo={campo}>
          <ComboReferencia
            id={id}
            valor={texto}
            onChange={onChange}
            endpoint={campo.refEndpoint ?? ''}
            chaveRotulo={campo.refRotulo}
            obrigatorio={campo.obrigatorio}
          />
        </Campo>
      );

    case 'dinheiro':
      return (
        <Campo id={id} campo={campo}>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[15px] text-text-tertiary">
              R$
            </span>
            <Input
              id={id}
              inputMode="numeric"
              required={campo.obrigatorio}
              placeholder="0,00"
              value={texto}
              onChange={(e) => onChange(mascaraDinheiro(e.target.value))}
              className="pl-9 text-right"
            />
          </div>
        </Campo>
      );

    case 'texto-longo':
      return (
        <Campo id={id} campo={campo}>
          <textarea
            id={id}
            required={campo.obrigatorio}
            placeholder={campo.placeholder}
            value={texto}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            className={cn(
              'w-full rounded-md border border-border bg-input px-3 py-2 text-[15px] text-text',
              'placeholder:text-text-tertiary transition-colors duration-[var(--dur-fast)] outline-none',
              'focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]',
            )}
          />
        </Campo>
      );

    default: {
      // texto | numero | data
      const tipoHtml = campo.tipo === 'numero' ? 'number' : campo.tipo === 'data' ? 'date' : 'text';
      // Sufixo derivado do valor (ex.: idade a partir da data de nascimento) — exibido DENTRO da
      // caixa do input, encostado à direita, para ler como parte do próprio campo.
      const sufixo = campo.sufixo ? campo.sufixo(texto) : null;
      const entrada = (
        <Input
          id={id}
          type={tipoHtml}
          required={campo.obrigatorio}
          placeholder={campo.placeholder}
          value={texto}
          onChange={(e) => onChange(e.target.value)}
          // Mutator (ex.: capitalizar nome) roda ao sair do campo — não atrapalha a digitação.
          onBlur={campo.mutator ? () => onChange(campo.mutator!(texto)) : undefined}
          className={sufixo ? 'pr-24' : undefined}
        />
      );
      return (
        <Campo id={id} campo={campo}>
          {sufixo ? (
            <div className="relative">
              {entrada}
              <span className="pointer-events-none absolute inset-y-px right-px flex items-center rounded-r-md border-l border-border bg-input px-3 text-[15px] text-text-secondary">
                {sufixo}
              </span>
            </div>
          ) : (
            entrada
          )}
        </Campo>
      );
    }
  }
}

/** Envoltório rótulo + ajuda para os campos que não trazem o próprio rótulo. */
function Campo<T extends RecursoBase>({
  id,
  campo,
  children,
}: {
  id: string;
  campo: DefCampo<T>;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{campo.rotulo}</Label>
      {children}
      {campo.ajuda && <p className="text-[12px] leading-[1.4] text-text-tertiary">{campo.ajuda}</p>}
    </div>
  );
}
